import AgTechGrid from "@agtech/web/components/Grids/AgTechGrid";
import { AgTechGridHeaderConfiguration } from "@agtech/web/components/Grids/AgTechGridHeader";
import { AgTechMoneyFormatter } from "@agtech/web/utilities/AgTechUtilities";
import { SaleInvoiceModifier } from "@sales/data/sales/InvoiceModifiers/SaleInvoiceModifierData";
import { useAgTechQuery, useAgTechQueryResultProjection } from "@agtech/core/data/queries/AgTechDataQueries";
import { InvoiceModifierDataGetAllInvoiceModifiersQuery } from "@sales/data/sales/InvoiceModifiers/SaleInvoiceModifierDataQueries";
import { AgTechGridEntitySelectionIndex, mergeGridEntitySelections } from "@agtech/web/components/Grids/AgTechGridEntities";
import { AgTechClickableIcon } from "@agtech/web/components/Icons/AgTechIcons";
import SaleInvoiceModifierDialog from "../../../invoicemodifiers/dialogs/SaleInvoiceModifierDialog";

export type InvoiceChangeInvoiceModifiersGridProps = {
    header?: AgTechGridHeaderConfiguration,
    InvoiceModifierSelections: AgTechGridEntitySelectionIndex<SaleInvoiceModifier>,
    onInvoiceModifierSelected: (d: SaleInvoiceModifier) => Promise<void>
    onInvoiceModifierDeselected: (d: SaleInvoiceModifier) => Promise<void>
}

const InvoiceChangeInvoiceModifiersGrid = (props: InvoiceChangeInvoiceModifiersGridProps) => {
    const gridQuery = useInvoiceInvoiceModifiersGridQuery(props);

    return (
        <AgTechGrid
            data={{
                itemQuery: gridQuery
            }}
            header={props.header}
            columns={[
                {
                    header: 'Name',
                    value: d => d.entity.name,
                    size: 3
                },
                {
                    header: 'Charges and discounts',
                    value: d => d.entity.isPercentage ?  `${d.entity.amount}%` : AgTechMoneyFormatter.format(d.entity.amount),
                    size: 1
                }
            ]}
            isRowEntityTheSame={(prev, curr) => {
                return prev.entity.name === curr.entity.name &&
                    prev.entity.amount === curr.entity.amount;
            }}
            selectionConfig={{
                isItemSelected: InvoiceModifier => InvoiceModifier.isSelected,
                onItemSelected: async selectedInvoiceModifier => {
                    props.onInvoiceModifierSelected(selectedInvoiceModifier.entity);
                },
                onItemDeSelected: async deselectedInvoiceModifier => {
                    props.onInvoiceModifierDeselected(deselectedInvoiceModifier.entity);
                }
            }}
            actions={{
                rendering: InvoiceModifierSelection => {
                    return (
                        <AgTechClickableIcon
                            icon='bi bi-pencil-square'
                            tooltip="Update charges and discounts"
                            onClick={e => SaleInvoiceModifierDialog.Open(e, {
                                InvoiceModifier: InvoiceModifierSelection.entity
                            })}
                        />
                    )
                }
            }}
        />
    )
}

const useInvoiceInvoiceModifiersGridQuery = (props: InvoiceChangeInvoiceModifiersGridProps) => {
    const saleInvoiceModifiersQuery = useAgTechQuery({
        query: InvoiceModifierDataGetAllInvoiceModifiersQuery
    });

    return useAgTechQueryResultProjection({
        queryRun: saleInvoiceModifiersQuery,
        projection: InvoiceModifiers => {
            return {
                items: mergeGridEntitySelections<SaleInvoiceModifier>({
                    entitySelections: props.InvoiceModifierSelections,
                    entities: Object.values(InvoiceModifiers)
                }),
                context: {
                    allInvoiceModifiers: InvoiceModifiers
                }
            }
        }
    });
}

export default InvoiceChangeInvoiceModifiersGrid;